/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type React from 'react';
import { cn } from '@/lib/utils';
import { DynamicImage } from '@/components/dynamic-image';
import { Link } from '@/components/link';
import { useAnalytics } from '@/hooks/use-analytics';
import { toImageUrl } from '@/lib/images/dynamic-image';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { useTranslation } from 'react-i18next';

interface Suggestion {
    name: string;
    link: string;
    type: string;
    image?: string;
    parentCategoryName?: string;
}

interface SuggestionsProps {
    suggestions?: Suggestion[];
    searchPhrase?: string;
    closeAndNavigate?: (link: string) => void;
    className?: string;
    /**
     * Row element. `'button'` (the DEFAULT) is the original behaviour and every
     * existing caller gets it without passing anything — the snapshot fixture
     * `stories/__snapshots__/suggestions-list-snapshot.tsx.snap` is the check
     * that this stayed true.
     *
     * `'link'` renders `<ul><li><a href>` instead, which is what live BG's
     * suggestion rows are (.reference/captures/bg-searchbar-m-375-b/App.tsx —
     * `<ul><li><a href="/search/">…</a></li>`, ten rows). Anchors are not a
     * styling preference: they are what makes middle-click and "open in new
     * tab" work on a row whose only job is to go somewhere.
     *
     * In link mode the ANCHOR performs the navigation, so `closeAndNavigate` is
     * invoked purely to let the caller close its surface — a caller that also
     * navigates from that callback would navigate twice. Modified clicks
     * (cmd/ctrl/shift/alt) skip the callback entirely so the panel survives an
     * open-in-new-tab.
     */
    as?: 'button' | 'link';
    /**
     * Bold the `searchPhrase` occurrence inside each row's name, live BG's
     * three-span prefix / **match** / suffix treatment (same capture; row 2 is
     * `**dress**es`, a two-span row, so the prefix span is genuinely optional).
     *
     * Defaults to `false` — off — which renders the single `<span>` every
     * existing caller already gets. It reuses the `searchPhrase` prop rather
     * than taking the term again, so there is one source of truth for what
     * "the query" is.
     */
    highlight?: boolean;
}

/**
 * Split `name` around the first case-insensitive occurrence of `phrase` into
 * live's three spans. No match (or no phrase) returns the single unstyled span,
 * which is the same output the un-highlighted row produces.
 */
function renderName(name: string, phrase: string | undefined, highlight: boolean) {
    if (!highlight || !phrase) {
        return <span className="text-sm font-medium">{name}</span>;
    }
    const at = name.toLowerCase().indexOf(phrase.toLowerCase());
    if (at === -1) {
        return <span className="text-sm font-medium">{name}</span>;
    }
    return (
        <span className="text-sm">
            {at > 0 && <span>{name.slice(0, at)}</span>}
            <span className="font-bold">{name.slice(at, at + phrase.length)}</span>
            {at + phrase.length < name.length && <span>{name.slice(at + phrase.length)}</span>}
        </span>
    );
}

const Suggestions: React.FC<SuggestionsProps> = ({
    suggestions,
    searchPhrase,
    closeAndNavigate,
    className,
    as = 'button',
    highlight = false,
}) => {
    const analytics = useAnalytics();
    const config = useConfig();
    const { t } = useTranslation('common');
    if (!suggestions || suggestions.length === 0) {
        return null;
    }

    const handleClick = (suggestion: Suggestion) => {
        void analytics.trackClickSearchSuggestion({
            searchInputText: searchPhrase || '',
            suggestion: suggestion.name,
        });
        if (closeAndNavigate) {
            closeAndNavigate(suggestion.link);
        }
    };

    if (as === 'link') {
        return (
            <ul data-testid="sf-suggestion" className={cn('space-y-0', className)}>
                {suggestions.map((suggestion) => (
                    <li key={suggestion.link}>
                        <Link
                            to={suggestion.link}
                            data-slot="suggestion"
                            onClick={(event) => {
                                // Let a modified click be a real new-tab open:
                                // no analytics close, no surface teardown.
                                if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                                handleClick(suggestion);
                            }}
                            className="w-full flex justify-start items-center py-0 text-left hover:bg-accent hover:text-foreground transition-colors text-sm">
                            {renderName(suggestion.name, searchPhrase, highlight)}
                        </Link>
                    </li>
                ))}
            </ul>
        );
    }

    return (
        <div data-testid="sf-suggestion" className={cn('space-y-0', className)}>
            <div>
                {suggestions.map((suggestion) => (
                    <button
                        key={suggestion.link}
                        data-slot="suggestion"
                        onMouseDown={() => handleClick(suggestion)}
                        className="w-full flex justify-start items-center pl-4 py-0 hover:bg-accent hover:text-foreground transition-colors text-sm mt-0">
                        <div className="flex items-center">
                            {suggestion.image && (
                                <div className="w-10 h-10 mr-4 bg-transparent flex items-center justify-center overflow-hidden shrink-0">
                                    <DynamicImage
                                        src={`${toImageUrl({ src: suggestion.image, config })}[?sw={width}]`}
                                        alt={suggestion.name || t('suggestionImageAlt')}
                                        className="w-full h-full"
                                        imageProps={{
                                            className: 'w-full h-full object-cover',
                                        }}
                                        loading="eager"
                                    />
                                </div>
                            )}
                            <div className="text-left">
                                <span className="text-sm font-medium">{suggestion.name}</span>
                            </div>
                        </div>
                    </button>
                ))}
            </div>
        </div>
    );
};

export default Suggestions;
